import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const cachedRecommendations = new Map();

class LordCartUpsell extends HTMLElement {
  #loading;
  #observer;
  #shownProductId;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    if (!this.dataset.productId) return;
    this.#observer = new MutationObserver(() => {
      if (!this.isConnected) return;
      if (this.#shownProductId !== this.dataset.productId) this.replaceChildren();
      if (this.querySelector('.lord-cart-upsell__card')) return;
      const cached = cachedRecommendations.get(this.dataset.productId);
      if (cached) this.#render(cached);
      else this.#load();
    });
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-product-id'] });
    const cached = cachedRecommendations.get(this.dataset.productId);
    if (cached) this.#render(cached);
    else this.#load();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick);
  }

  async #getCards(intent, productId) {
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', 'lord-cart-upsell');
    url.searchParams.set('intent', intent);
    url.searchParams.set('limit', '10');
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Recommendations could not load');
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...document.querySelectorAll('[data-lord-upsell-items] .lord-cart-upsell__card')]
      .filter((card) => card.dataset.productId !== productId)
      .slice(0, 3);
  }

  #render(cards) {
    if (!this.isConnected) return;
    this.#shownProductId = this.dataset.productId;
    const panel = document.createElement('section');
    panel.className = 'lord-cart-upsell__panel';
    panel.innerHTML = `<div class="lord-cart-upsell__head">
      <h3 class="lord-cart-upsell__title">اقتراحات تكمل طلبك</h3>
      <div class="lord-cart-upsell__controls" dir="rtl">
        <button type="button" data-upsell-scroll="previous" aria-label="الاقتراح السابق">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
        </button>
        <button type="button" data-upsell-scroll="next" aria-label="الاقتراح التالي">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg>
        </button>
      </div>
    </div><div class="lord-cart-upsell__list" role="region" tabindex="0" aria-label="المنتجات المقترحة، مرّر أفقيًا لعرض المزيد"></div><p class="lord-cart-upsell__status" role="status" aria-live="polite"></p>`;
    panel.querySelector('.lord-cart-upsell__list').append(...cards.map((card) => card.cloneNode(true)));
    this.replaceChildren(panel);
  }

  async #load() {
    if (this.#loading) return;
    this.#loading = true;
    const productId = this.dataset.productId;
    try {
      let cards = await this.#getCards('complementary', productId);
      if (!cards.length) cards = await this.#getCards('related', productId);
      if (!cards.length || !this.isConnected || productId !== this.dataset.productId) return;
      cachedRecommendations.set(productId, cards.map((card) => document.importNode(card, true)));
      this.#render(cachedRecommendations.get(productId));
    } catch (error) {
      // Suggestions are optional; cart and checkout remain available.
      console.warn('[lord-cart-upsell]', error);
    } finally {
      this.#loading = false;
      if (this.isConnected && productId !== this.dataset.productId) this.#load();
    }
  }

  #onClick = async (event) => {
    const scrollButton = event.target.closest('button[data-upsell-scroll]');
    if (scrollButton && this.contains(scrollButton)) {
      const list = this.querySelector('.lord-cart-upsell__list');
      const card = list?.querySelector('.lord-cart-upsell__card');
      if (list && card) {
        const distance = card.getBoundingClientRect().width + 8;
        list.scrollBy({
          left: (scrollButton.dataset.upsellScroll === 'next' ? -1 : 1) * distance,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
      return;
    }
    const button = event.target.closest('button[data-variant-id]');
    if (!button || !this.contains(button) || button.disabled) return;
    const variantId = Number(button.dataset.variantId);
    if (!Number.isSafeInteger(variantId)) return;
    button.disabled = true;
    const status = this.querySelector('.lord-cart-upsell__status');
    if (status) status.textContent = 'جارٍ الإضافة…';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId).filter(Boolean))];
      if (!sections.includes('cart-drawer-section')) sections.push('cart-drawer-section');
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'تعذر إضافة المنتج');
      const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error('تعذر تحديث السلة');
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          source: 'lord-cart-upsell',
          sections: result.sections,
          items: cart.items,
          itemCount: cart.item_count,
        },
      });
      if (status) status.textContent = 'تمت الإضافة إلى السلة';
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
      if (status) status.textContent = error.message || 'تعذر إضافة المنتج';
    } finally {
      button.disabled = false;
    }
  };
}

if (!customElements.get('lord-cart-upsell')) customElements.define('lord-cart-upsell', LordCartUpsell);
